/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  TableContainer,
  Link,
  Tooltip,
} from '@mui/material';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import { DownloadOutlined } from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { transformTransactionToInvoice } from 'renderer/features/payments-page/schemas/transaction.transformer';
import { TransactionDetails } from 'renderer/features/payments-page/schemas/transaction.schemas';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import _ from 'lodash';
import ExportPDFView from './export-pdf';

const tableStyles = {
  tableContainer: {
    // marginTop: '20px',
    marginBottom: '20px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
};

const PDFIconButton = ({
  transactionGroup,
}: {
  transactionGroup: TransactionDetails[];
}) => {
  const theme = useTheme();
  const transactionInfo = transformTransactionToInvoice(transactionGroup);
  return (
    <PDFDownloadLink
      document={<ExportPDFView transaction={transactionInfo} />}
      fileName={`${transactionInfo.id}.pdf`}
    >
      <Tooltip title="Download" placement="top">
        <IconButton>
          <DownloadOutlined style={{ color: theme.palette.grey[900] }} />
        </IconButton>
      </Tooltip>
    </PDFDownloadLink>
  );
};

interface PaymentsProps {
  payments: TransactionDetails[];
  handleOpenModal: (serials: string[]) => void;
  handleOpenProductModal: (products: string[]) => void;
}

const Payments: React.FC<PaymentsProps> = ({
  payments,
  handleOpenModal,
  handleOpenProductModal,
}) => {
  const groupPayments = (data: TransactionDetails[]) => {
    const grouped = _.groupBy(data, (p) => p.cartId ?? p._id.toString());

    return Object.values(grouped).map((group) => {
      const first = group[0];
      return {
        ...first,
        originalGroup: group, // pass this down
        isGrouped: group.length > 1,
        groupedProducts: group.map((t) => t.productName),
        groupedCategories: _.uniq(group.map((t) => t.category)),
        groupedSerials: group.flatMap((t) => t.serials),
        groupedTotal: _.sumBy(group, (t) => t.amount),
      };
    });
  };

  return (
    <TableContainer component={Paper} sx={tableStyles.tableContainer}>
      <Table sx={tableStyles.table} aria-label="licenses table" size="small">
        <TableHead sx={tableStyles.tableHead}>
          <TableRow>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              PRODUCT
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              CATEGORY
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              DATE
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              UNIT PRICE
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              QUANTITY
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              TOTAL
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              PDF
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              LICENSE KEY
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupPayments(payments).map((payment) => (
            <TableRow key={payment._id}>
              <TableCell align="center">
                {payment.groupedProducts[0]}
                {payment.isGrouped && payment.groupedProducts && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() =>
                      handleOpenProductModal(
                        payment.groupedProducts.filter(
                          (p): p is string => typeof p === 'string',
                        ),
                      )
                    }
                    sx={{ textDecoration: 'underline', ml: 1 }}
                  >
                    +{payment.groupedProducts.length - 1}
                  </Link>
                )}
              </TableCell>

              <TableCell align="center">
                {/* {payment.groupedCategories.length > 1
                  ? 'Plugin and Subscription'
                  : payment.groupedCategories[0]} */}
                {payment.groupedCategories.length > 1
                  ? payment.groupedCategories.includes('Bundle') &&
                    payment.groupedCategories.includes('Subscription')
                    ? 'Bundle and Subscription'
                    : payment.groupedCategories.includes('Bundle')
                    ? 'Bundle'
                    : payment.groupedCategories.includes('Subscription')
                    ? 'Subscription'
                    : 'Plugin'
                  : payment.groupedCategories[0]}
              </TableCell>

              <TableCell align="center">
                {moment(payment.createdAt).format('MM-DD-YYYY')}
              </TableCell>

              <TableCell align="center">
                {payment.isGrouped ? '-' : `$${payment.fullPrice}`}
              </TableCell>

              <TableCell align="center">{payment.quantity}</TableCell>

              <TableCell align="center">
                {formatCurrency(payment.groupedTotal)}
              </TableCell>

              <TableCell align="center">
                <PDFIconButton transactionGroup={payment.originalGroup} />
              </TableCell>

              <TableCell align="center">
                {payment.groupedSerials[0]}
                {payment.groupedSerials.length > 1 && (
                  <Link
                    component="button"
                    onClick={() => handleOpenModal(payment.groupedSerials)}
                    sx={{ textDecoration: 'underline', ml: 1 }}
                  >
                    +{payment.groupedSerials.length - 1}
                  </Link>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Payments;
